import { Component, EventEmitter, inject, Input, Output } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { OrderTicket, TicketStatusEnum } from "@models/ticket-order";
import { TicketManagementService } from "@services/ticket-management.service";
import {
  TicketAssignDialogComponent,
  TicketAssignDialogData,
} from "@shared/components/ticket-dialogs/ticket-assign-dialog/ticket-assign-dialog.component";
import {
  TicketDetailsDialogComponent,
  TicketDetailsDialogData,
} from "@shared/components/ticket-dialogs/ticket-details-dialog/ticket-details-dialog.component";
import {
  TicketTransferDialogComponent,
  TicketTransferDialogData,
} from "@shared/components/ticket-dialogs/ticket-transfer-dialog/ticket-transfer-dialog.component";
import { ConfirmActionModalComponent } from "@shared/modals/confirm-action-modal/confirm-action-modal.component";
import { finalize } from "rxjs";
import { ButtonComponent } from "../../../../../../shared/components/button/button.component";
import { LoadingComponent } from "../../../../../../shared/components/loading/loading.component";
import { UtilsService } from "../../../../../../utils/utils.service";
import { LocalStorageService } from "../../../../../services/local-storage.service";
import { SnackBarService } from "../../../../../services/snack-bar.service";
import { TicketPdfService } from "../../../../../services/ticket-pdf.service";
import { TransfersService } from "../../../../../services/transfers.service";

@Component({
  selector: "app-ticket-card",
  imports: [ButtonComponent, LoadingComponent],
  templateUrl: "./ticket-card.component.html",
  styleUrl: "./ticket-card.component.scss",
})
export class TicketCardComponent {
  @Input({ required: true }) ticket!: OrderTicket;

  @Input() restrictToEventAction = false;

  currentUser = JSON.parse(
    this.localStorageService.getItem("current_user") ?? "false",
  );

  @Output()
  reloadTicketsAfterChange = new EventEmitter();

  loading = false;

  readonly ticketService = inject(TicketManagementService);

  constructor(
    private localStorageService: LocalStorageService,
    private snackbarService: SnackBarService,
    private readonly router: Router,
    private utilsService: UtilsService,
    private transferService: TransfersService,
    private dialog: MatDialog,
    private ticketPdfService: TicketPdfService,
  ) {}

  readonly TicketStatusEnum = TicketStatusEnum;

  revokeTransfer() {
    if (this.ticket.status !== TicketStatusEnum.TransferInProgress) {
      return;
    }

    const transferId = this.ticket.transfer_id;

    const dialogRef = this.dialog.open(ConfirmActionModalComponent, {
      width: "500px",
      data: {
        modalTitle: "Cancelar transferência?",
        modalContent: `Deseja realmente cancelar essa transferência?`,
        confirmMessage: "Confirmar",
        cancelMessage: "Cancelar",
      },
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this.transferService
          .deleteTransfer(transferId)
          .subscribe(() => this.reloadTicketsAfterChange.emit());
      }
    });
  }

  openAssignModal(ticket: OrderTicket): void {
    const data: TicketAssignDialogData = {
      ticket,
      currentUser: this.currentUser,
    };

    const dialogRef = this.dialog.open(TicketAssignDialogComponent, {
      width: "800px",
      maxWidth: "95vw",
      data,
    });

    dialogRef.afterClosed().subscribe((shouldReload) => {
      if (shouldReload) {
        this.reloadTicketsAfterChange.emit();
      }
    });
  }

  openTransferModal(ticket: OrderTicket): void {
    const data: TicketTransferDialogData = { ticket };

    const dialogRef = this.dialog.open(TicketTransferDialogComponent, {
      width: "800px",
      maxWidth: "95vw",
      data,
    });

    dialogRef.afterClosed().subscribe((shouldReload) => {
      if (shouldReload) {
        this.reloadTicketsAfterChange.emit();
      }
    });
  }

  openTicketInfoModal(ticket: OrderTicket): void {
    this.loading = true;

    this.ticketService
      .getTicketWithQrCode(ticket.id)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe((ticketWithQrCode) => {
        const data: TicketDetailsDialogData = { ticket: ticketWithQrCode };

        this.dialog.open(TicketDetailsDialogComponent, {
          width: "800px",
          maxWidth: "95vw",
          data,
        });
      });
  }

  goToEvent(event_id: number) {
    this.router.navigate([`/event/${event_id}`]);
  }

  hasUserInfo() {
    const {
      user_cpf,
      user_identity_card,
      user_name,
      user_email,
      user_last_name,
    } = this.ticket;
    const hasDocument = !!user_cpf || !!user_identity_card;
    return hasDocument && !!user_name && !!user_email && !!user_last_name;
  }

  isTransfer() {
    const { status } = this.ticket;
    return (
      status === TicketStatusEnum.TransferInProgress ||
      status === TicketStatusEnum.Transferred
    );
  }

  getTicketStatus(): string {
    switch (this.ticket.status) {
      case TicketStatusEnum.Active:
      case TicketStatusEnum.NoIssues:
        return "Pronto para usar!";
      case TicketStatusEnum.Transferred:
        return "Ingresso transferido";
      case TicketStatusEnum.PaymentNotCompleted:
        return "Aguardando pagamento";
      case TicketStatusEnum.MissingUserInformation:
        return "Faltam informações do usuário";
      case TicketStatusEnum.PendingIssues:
        return "Em análise, em breve você terá novidades";
      case TicketStatusEnum.AlreadyUsed:
        return "Ingresso já utilizado";
      case TicketStatusEnum.TransferInProgress:
        return "Em processo de transferência";
      default:
        return "Indefinido";
    }
  }

  getStatusClass(): string {
    switch (this.ticket.status) {
      case TicketStatusEnum.Active:
      case TicketStatusEnum.NoIssues:
        return "no-issues";
      case TicketStatusEnum.Transferred:
        return "transferred";
      case TicketStatusEnum.PaymentNotCompleted:
        return "payment-not-completed";
      case TicketStatusEnum.MissingUserInformation:
        return "missing-user-information";
      case TicketStatusEnum.PendingIssues:
        return "pending-issues";
      case TicketStatusEnum.AlreadyUsed:
        return "already-used";
      case TicketStatusEnum.TransferInProgress:
        return "transfer-in-progress";
      default:
        return "pending-issues";
    }
  }

  eventDateHasPassed() {
    return this.utilsService.isPast(this.ticket.event_end_date);
  }

  /** Espelha EventActionCutoff da API: envio de transferência até 24h antes do início. */
  transferWindowClosed(): boolean {
    if (!this.ticket.event_start_date) return true;
    const start = new Date(this.ticket.event_start_date).getTime();
    if (Number.isNaN(start)) return true;
    return Date.now() >= start - 24 * 60 * 60 * 1000;
  }

  downloadTicketPdf(): void {
    this.loading = true;

    this.ticketService
      .getTicketWithQrCode(this.ticket.id)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (ticketWithQrCode) => {
          this.ticketPdfService
            .downloadTicketPdf(ticketWithQrCode, ticketWithQrCode.qr_code)
            .then(() => {
              this.snackbarService.openSnackBar(
                "PDF baixado com sucesso!",
                "Fechar",
                "success",
              );
            })
            .catch((error) => {
              console.error("Error downloading PDF:", error);
              this.snackbarService.openSnackBar(
                "Erro ao gerar PDF. Tente novamente.",
                "Fechar",
                "error",
              );
            });
        },
        error: (error) => {
          console.error("Error fetching ticket:", error);
          this.snackbarService.openSnackBar(
            "Erro ao buscar dados do ingresso. Tente novamente.",
            "Fechar",
            "error",
          );
        },
      });
  }
}
